import { ImageOptimizedPropsType, useLocales } from "@/lib/main";
import { type CSSProperties, useCallback, useEffect, useState } from "react";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_BREAKPOINTS = [384, 640, 768, 1024, 1280, 1536, 1920];

const BORDER_RADIUS_MAP: Record<string, string> = {
  "2xs": "var(--border-radius-2xs)",
  xs: "var(--border-radius-xs)",
  sm: "var(--border-radius-sm)",
  md: "var(--border-radius-md)",
  lg: "var(--border-radius-lg)",
  xl: "var(--border-radius-xl)",
  circle: "50%",
};

/** Preferred decode order — most efficient format first. */
const FORMAT_PRIORITY: Record<string, number> = {
  avif: 0,
  webp: 1,
  png: 2,
  jpg: 3,
  jpeg: 3,
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ImageOptimized({
  alt,
  aspectRatio,
  blurDataURL,
  borderRadius,
  breakpoints = DEFAULT_BREAKPOINTS,
  className,
  display,
  fallbackSrc,
  fetchPriority,
  file,
  fill,
  format,
  formats,
  height,
  loading,
  objectFit,
  objectPosition,
  onError,
  onLoad,
  placeholder = "empty",
  placeholderColor,
  sizes,
  style,
  wrapperStyle,
  width,
}: ImageOptimizedPropsType) {
  const l = useLocales();
  const [isLoaded, setIsLoaded] = useState(false);
  const [errorStage, setErrorStage] = useState(0);

  // Callback ref: detect already-cached images so we never flash a placeholder.
  const imgCallbackRef = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete && node.naturalWidth > 0) {
      setIsLoaded(true);
    }
  }, []);

  // ---- Derived values ----------------------------------------------------

  const primaryFormat = format || "webp";
  const resolvedBorderRadius = borderRadius
    ? BORDER_RADIUS_MAP[borderRadius] || borderRadius
    : undefined;

  /** Build a responsive srcSet string for a given image format. */
  const buildSrcSet = useCallback(
    (fmt: string) =>
      breakpoints.map((bp) => `${file}-${bp}w.${fmt} ${bp}w`).join(", "),
    [file, breakpoints],
  );

  // Pick the breakpoint closest to the declared intrinsic width for the
  // fallback `src` (used when srcSet isn't supported).
  const intrinsicWidth = width || 1024;
  const closestBreakpoint = breakpoints.reduce((prev, curr) =>
    Math.abs(curr - intrinsicWidth) < Math.abs(prev - intrinsicWidth)
      ? curr
      : prev,
  );
  const defaultSrc = `${file}-${closestBreakpoint}w.${primaryFormat}`;
  const computedSizes = sizes || "100vw";

  // ---- Side effects ------------------------------------------------------

  // Inject a <link rel="preload"> for high-priority images so the browser
  // can start fetching before it parses the <img> tag.
  useEffect(() => {
    if (fetchPriority !== "high") return;

    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = defaultSrc;
    link.setAttribute("imagesrcset", buildSrcSet(primaryFormat));
    link.setAttribute("imagesizes", computedSizes);
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, [fetchPriority, defaultSrc, buildSrcSet, primaryFormat, computedSizes]);

  // ---- Event handlers ----------------------------------------------------

  const handleLoad = useCallback(() => {
    setIsLoaded(true);
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setErrorStage((prev) => {
      if (prev === 1) onError?.();
      return prev < 2 ? prev + 1 : 2;
    });
  }, [onError]);

  // ---- Computed sources --------------------------------------------------

  const baseSrc = `${file}.${primaryFormat}`;
  const effectiveSrc =
    errorStage === 0
      ? defaultSrc
      : errorStage === 1
        ? baseSrc
        : (fallbackSrc ?? baseSrc);

  // ---- Styles ------------------------------------------------------------

  const hasPlaceholder = placeholder !== "empty";
  const showPlaceholder = hasPlaceholder && !isLoaded && errorStage < 2;

  const isContain = objectFit === "contain";

  const wrapperStyles: CSSProperties = {
    position: fill ? "absolute" : "relative",
    overflow: "hidden",
    display: display || (isContain && fill ? "flex" : "block"),
    borderRadius: resolvedBorderRadius,
    ...(isContain && fill
      ? {
          inset: 0,
          width: "100%",
          height: "100%",
          alignItems: "center",
          justifyContent: "center",
        }
      : fill
        ? { inset: 0, width: "100%", height: "100%" }
        : { ...(aspectRatio ? { aspectRatio } : {}) }),
    ...wrapperStyle,
  };

  const imgStyles: CSSProperties = {
    display: "block",
    ...(isContain && fill
      ? { maxWidth: "100%", maxHeight: "100%", width: "auto", height: "auto" }
      : { width: "100%", height: fill ? "100%" : "auto" }),
    objectFit: isContain
      ? undefined
      : objectFit || (fill ? "cover" : undefined),
    objectPosition: objectPosition,
    borderRadius: resolvedBorderRadius,
    // Smooth reveal when a placeholder is active
    ...(hasPlaceholder
      ? { opacity: isLoaded ? 1 : 0, transition: "opacity 0.4s ease-in-out" }
      : {}),
    ...style,
  };

  // ---- Placeholder -------------------------------------------------------

  const placeholderEl = showPlaceholder ? (
    <div
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        transition: "opacity 0.4s ease-in-out",
        ...(placeholder === "blur" && blurDataURL
          ? {
              backgroundImage: `url(${blurDataURL})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              filter: "blur(20px)",
              transform: "scale(1.1)",
            }
          : placeholder === "color"
            ? { backgroundColor: placeholderColor || "#e2e8f0" }
            : {}),
      }}
    />
  ) : null;

  // ---- <picture> format negotiation --------------------------------------

  // Sort additional formats by compression efficiency (best first) so the
  // browser picks the most efficient supported format.
  const allFormats = formats || [primaryFormat];
  const sourceFormats = [...allFormats]
    .filter((fmt) => fmt !== primaryFormat)
    .sort((a, b) => (FORMAT_PRIORITY[a] ?? 99) - (FORMAT_PRIORITY[b] ?? 99));
  const usesPicture = sourceFormats.length > 0;

  // ---- Render ------------------------------------------------------------

  const imgElement = (
    <img
      ref={imgCallbackRef}
      alt={l(alt)}
      className={className}
      decoding={fetchPriority === "high" ? "sync" : "async"}
      fetchPriority={fetchPriority}
      height={fill ? undefined : height || 576}
      loading={fetchPriority === "high" ? "eager" : loading || "lazy"}
      onError={handleError}
      onLoad={handleLoad}
      sizes={computedSizes}
      src={effectiveSrc}
      srcSet={errorStage === 0 ? buildSrcSet(primaryFormat) : undefined}
      style={imgStyles}
      width={fill ? undefined : width || 1024}
    />
  );

  const pictureContent = usesPicture ? (
    <picture>
      {sourceFormats.map((fmt) => (
        <source
          key={fmt}
          type={`image/${fmt === "jpg" ? "jpeg" : fmt}`}
          srcSet={buildSrcSet(fmt)}
          sizes={computedSizes}
        />
      ))}
      {imgElement}
    </picture>
  ) : (
    imgElement
  );

  return (
    <div style={wrapperStyles}>
      {placeholderEl}
      {pictureContent}
    </div>
  );
}
